import { min, max } from 'd3-array';
import { deepClone } from '../util/deepClone';

import Indicator from '../Indicator';
import BarMarker from '../markers/BarMarker';
import LineMarker from '../markers/LineMarker';
import subpixel from '../util/canvasSubpixelOffset';

/** MACD indicator */
export default class MACDIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {string} markerType - Indicator marker type - 'default' | 'histogram'
	 */
	_getDefaultParams() {
		return {
			id: 'macd',
			name: 'MACD',
			description:
				'The MACD is the difference between 2 exponential moving averages and is a trend and momentum indicator. It is normally considered bullish when the MACD line is above the signal line and more so when both are above the 0-line.',
			inputs: [
				{ name: 'fastperiod', value: 12 },
				{ name: 'slowperiod', value: 26 },
				{ name: 'smoothing', value: 9 }
			],
			markerType: 'default', // "default", "histogram"
			componentSeries: [
				{ id: 'macd' },
				{ id: 'ema', styleRef: 'ema' }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		if (this.params.markerType === 'histogram') {
			const dataCopy = deepClone(data);
			const panelRange = this.panel.xAxis.primaryScale.domain();

			// Remove first element when returnPreviousPoint is selected so panel size is not affected
			if (this.panel.core.params.returnPreviousPoint) {
				dataCopy.macd.shift();
			}

			//Remove last element if it is out of view in panel so range size is not affected
			if (dataCopy.macd[dataCopy.macd.length - 1].dateIndex > panelRange[1]) {
				dataCopy.macd.pop();
			}

			return this._min(
				min(dataCopy.macd || [], i => i.divergence),
				0
			);
		}

		return this._min(
			min(data.macd || [], i => +i.ema),
			min(data.macd || [], i => +i.divergence),
			min(data.macd || [], i => +i.macd),
			0
		);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		if (this.params.markerType === 'histogram') {
			const dataCopy = deepClone(data);
			const panelRange = this.panel.xAxis.primaryScale.domain();

			// Remove first element when returnPreviousPoint is selected so panel size is not affected
			if (this.panel.core.params.returnPreviousPoint) {
				dataCopy.macd.shift();
			}

			//Remove last element if it is out of view in panel so range size is not affected
			if (dataCopy.macd[dataCopy.macd.length - 1].dateIndex > panelRange[1]) {
				dataCopy.macd.pop();
			}

			return this._max(
				max(dataCopy.macd || [], i => i.divergence),
				0
			);
		}

		return this._max(
			max(data.macd || [], i => +i.ema),
			max(data.macd || [], i => +i.divergence),
			max(data.macd || [], i => +i.macd),
			0
		);
	}

	/**
	 * @returns {object}
	 * @property {LineMarker} lineEMA
	 * @property {LineMarker} lineMACD
	 * @property {BarMarker} barDivUp
	 * @property {BarMarker} barDivDown
	 */
	_getMarkers() {
		return (
			this.markers || {
				lineEMA: new LineMarker(),
				lineMACD: new LineMarker(),
				barDivUp: new BarMarker(),
				barDivDown: new BarMarker()
			}
		);
	}

	/**
	 * @param {object} data - Indicator data
	 * @param {string} type - indicator type
	 * @return {array[]}
	 */
	_getDataMap(data = [], type = null) {
		switch (type) {
			case 'macd':
				return data.map(d => [d.dateIndex, +d.macd]);

			case 'ema':
				return data.map(d => [d.dateIndex, +d.ema]);

			case 'divUp':
				return data.map(d => [d.dateIndex, d.divergence >= 0 ? d.divergence : 0]);

			case 'divDown':
				return data.map(d => [d.dateIndex, d.divergence < 0 ? d.divergence : 0]);

			default:
				return [];
		}
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const ctx = this.panel.rootContext;
		const x = this.xScale;
		const y = this.yScale;
		const style = this.params.style;

		this.markers.barDivUp.render(
			this.panel,
			ctx,
			this._getDataMap(data.macd, 'divUp'),
			x,
			y,
			style.positive.color
		);

		this.markers.barDivDown.render(
			this.panel,
			ctx,
			this._getDataMap(data.macd, 'divDown'),
			x,
			y,
			style.negative.color
		);

		if (this.params.markerType !== 'histogram') {

			this._renderLineMarker({
				data: data.macd,
				sessionType,
				marker: this.markers.lineEMA,
				dataKey: 'ema'
			});

			this._renderLineMarker({
				data: data.macd,
				sessionType,
				marker: this.markers.lineMACD,
				dataKey: 'macd'
			});
		}

		// zero line
		ctx.beginPath();
		const yZero = subpixel.px(y(0.0));
		ctx.strokeStyle = style.zero.color;
		ctx.lineWidth = style.zero.width;
		if (style.zero.lineDash) {
			ctx.setLineDash(style.zero.lineDash.split(/\s*,\s*/));
		}
		ctx.moveTo(this.panel.size.padding.left, yZero);
		ctx.lineTo(
			this.panel.size.padding.left + this.panel.size.width,
			yZero
		);
		ctx.stroke();
		ctx.setLineDash([]);
	}
}
